import React, { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendCompany,
	FrontendCompanyWithAdditionalInfomation,
	FrontendAnalystCustomGroup,
} from "node-library/dist/portal";
import {
	OrderTableSortColumn,
	SortOrder,
	UserTableSortColumn,
} from "node-library/dist/paginations";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { Severity, UserStatus, SystemRole } from "node-library/dist/constants";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import {
	getUserRoleString,
	getUserSortString,
	getUserStatusString,
} from "../../../helpers/userHelper";
import {
	CustomRadioGroup,
	CustomSearchBar,
	RowDropdown,
} from "../../../common-components/inputs";
import { SortArrowIcon } from "../../../common-components/svgIcons";
import { Pagination } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import RosterUserContent from "./rosterUserContent";
import UserDetailsContent from "./userDetailsContent";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import ViewOrderPageSkeletonUI from "../viewOrders/skeletonUI/orderHistoryPageSkeleton";
import { BlackButton } from "../../../common-components/button";
import AddIcon from "@mui/icons-material/Add";
import AddOrEditUserModal from "./modal/addOrEditUserModal";
import DeleteUserConfirmModal from "./modal/deleteUserConfirmModal";
import { AnalystCustomGroup } from "node-library/dist/database";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	user: FrontendUserInfo | undefined;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function RosterPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

	const [users, setUsers] = useState<Array<FrontendUserInfo>>([]);

	const [companyCustomGroups, setCompanyCustomGroups] = useState<
		Array<FrontendAnalystCustomGroup>
	>([]);

	const [selectedUser, setSelectedUser] = useState<FrontendUserInfo>();

	const [loading, setLoading] = useState<boolean>(false);
	const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

	const [adminInAction, setAdminInAction] = useState<boolean>(false);
	const [searchText, setSearchText] = useState<string>("");

	//paginations
	const [totalPageCount, setTotalPageCount] = useState<number>(0);
	const [sortBy, setSortBy] = useState<UserTableSortColumn>(
		UserTableSortColumn.INSERTED
	);
	const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);
	const [currentPagination, setCurrentPagination] = useState<number>(1);

	const [roleFilterOption, setRoleFilterOption] = useState<SystemRole | "">("");

	const [addEditUserModalOpen, setAddEditUserModalOpen] =
		useState<boolean>(false);

	const [editUserPermissionModalOpen, setEditUserPermissionModalOpen] =
		useState<boolean>(false);
	//confirm modals
	const [deleteUserConfirmOpen, setDeleteUserConfirmOpen] =
		useState<boolean>(false);
	const [proceedAction, setProceedAction] = useState<() => void>(
		() => () => {}
	);

	function toggleSortOrder() {
		if (sortOrder === SortOrder.ASC) {
			setSortOrder(SortOrder.DESC);
		} else {
			setSortOrder(SortOrder.ASC);
		}
	}

	const updateUser = (updatedUser: FrontendUserInfo) => {
		setUsers((prevUsers) =>
			prevUsers.map((user) =>
				user.userId === updatedUser.userId ? { ...user, ...updatedUser } : user
			)
		);
	};
	async function getUserAndGroups(searchText: string) {
		setLoading(true);
		try {
			const response = await sendGETRequest("/users/get-users-and-groups", {
				searchText,
				sortBy,
				sortOrder,
				pageNumber: currentPagination,
				roleFilterOption,
				shouldGetGroups: isInitialLoad,
			});

			const users: Array<FrontendUserInfo> = response?.data?.users;
			const pageCount: number = response?.data?.totalPages;
			setUsers(users);
			setTotalPageCount(pageCount);
			if (isInitialLoad) {
				const groups: Array<FrontendAnalystCustomGroup> =
					response?.data?.groups;
				setCompanyCustomGroups(groups);
			}
			setIsInitialLoad(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function createUser(
		user: FrontendUserInfo,
		password: string,
		role: SystemRole
	) {
		setAdminInAction(true);
		try {
			const response = await sendPOSTRequest("/users/create-user", {
				newUser: user,
				password,
				role,
			});

			openSnackbar(
				translation("UserCreated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			goBackToViewUsersPage(true);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setAdminInAction(false);
		}
	}

	async function editUser(
		user: FrontendUserInfo,
		password: string,
		role: SystemRole
	) {
		setAdminInAction(true);
		try {
			const response = await sendPOSTRequest("/users/update-user", {
				newUser: user,
				password,
				role,
			});

			openSnackbar(
				translation("UserUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setAddEditUserModalOpen(false);
			updateUser(user);
			setSelectedUser(user);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setAdminInAction(false);
		}
	}

	const roleSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
		children?: any;
	}> = Object.values(SystemRole).map((role: SystemRole) => {
		const name: string = getUserRoleString(role, translation);
		return {
			key: role,
			name: name,
			description: "",
			value: role,
			children: (
				<div
					className={`center-text status-selection-radio-option typography-body-02 ${
						roleFilterOption === role
							? "status-selection-radio-option-selected"
							: ""
					}`}
				>
					{name}
				</div>
			),
		};
	});
	roleSelectionOptions.unshift({
		key: "All",
		name: translation("All"),
		description: "",
		value: "",
		children: (
			<div
				className={`center-text status-selection-radio-option ${
					roleFilterOption === ""
						? "status-selection-radio-option-selected"
						: ""
				}`}
			>
				{translation("All")}
			</div>
		),
	});
	const statusSelection: React.ReactNode = (
		<div
			style={{
				maxWidth: "800px",
				overflowX: "auto",
			}}
		>
			<CustomRadioGroup
				options={roleSelectionOptions}
				selectedOption={roleFilterOption}
				onChange={(e) => {
					setRoleFilterOption(e.target.value);
				}}
				className="status-selection-radio-group"
			/>
		</div>
	);

	const sortOptionSelection: React.ReactNode = (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "5px",
				marginLeft: "auto",
			}}
		>
			<div
				onClick={() => {
					toggleSortOrder();
				}}
				style={{ cursor: "pointer" }}
			>
				<SortArrowIcon sortOrder={sortOrder} />
			</div>
			<RowDropdown
				id="order-sort-order-dropdown"
				className="sort-order-dropdown"
				label={"a"}
				labelClassName="hide"
				options={Object.values(UserTableSortColumn).map((value) => {
					return {
						value: value,
						title: getUserSortString(value, translation),
					};
				})}
				value={sortBy}
				setValue={setSortBy}
				noBorder
			></RowDropdown>
		</div>
	);

	useEffect(() => {
		getUserAndGroups(searchText);
	}, [currentPagination, sortBy, sortOrder, roleFilterOption]);

	function gotoUserDetailsPage(user: FrontendUserInfo) {
		setSelectedUser(user);
		setCurrentPageIndex(1);
	}
	function goBackToViewUsersPage(reload: boolean) {
		setCurrentPageIndex(0);
		setSelectedUser(undefined);
		if (reload) {
			getUserAndGroups(searchText);
		}
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "20px",
						minHeight: "90vh",
						marginTop: "60px",
					}}
				>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "column",
							gap: "20px",
						}}
					>
						<div className="view-orders-page-header">
							<div className="typography-heading-01">
								{translation("Roster")}
							</div>
							<CustomSearchBar
								id="view-order-page-searchbar"
								searchText={searchText}
								setSearchText={setSearchText}
								placeholder={translation("SearchByUserNameOrGroupName")}
								searchButtonText={translation("Search")}
								onSearch={() => {
									if (currentPagination !== 1) {
										setCurrentPageIndex(1);
									} else {
										getUserAndGroups(searchText);
									}
								}}
							></CustomSearchBar>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "10px",
							}}
						>
							{statusSelection}

							{sortOptionSelection}
						</div>
						<div style={{ display: "flex" }}>
							<BlackButton
								key="add-client-button"
								id="add-client-button"
								size="medium"
								children={
									<>
										<AddIcon></AddIcon>
										{translation("User")}
									</>
								}
								onClick={() => {
									setAddEditUserModalOpen(true);
								}}
							></BlackButton>
						</div>

						{loading ? (
							<ViewOrderPageSkeletonUI />
						) : (
							<RosterUserContent
								users={users}
								goToViewUserDetails={gotoUserDetailsPage}
								setDeleteUserConfirmOpen={setDeleteUserConfirmOpen}
								setProceedAction={setProceedAction}
							/>
						)}
					</div>
					{!loading && users.length > 0 && (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Pagination
								count={totalPageCount}
								page={currentPagination}
								onChange={(e, value) => {
									setCurrentPagination(value);
								}}
								sx={{
									backgroundColor: "white",
									borderRadius: "30px",
									padding: "6px 16px",

									"& .MuiPaginationItem-root": {
										backgroundColor: "white",
										color: "black",
										border: "1px solid transparent", // Hide outline when not selected
										"&:hover": {
											backgroundColor: "#f5f5f5", // Change background on hover
										},
										borderRadius: "50%",
									},
									"& .Mui-selected": {
										backgroundColor: SecondaryColor.SECONDARY_50, // Change background for selected item
										border: `1px solid ${NeutralColor.NEUTRAL_GREY_950}`, // Change text color for selected item
									},
								}}
								variant="outlined"
								shape="rounded"
							/>
						</div>
					)}
				</div>
			);
			break;
		case 1:
			currentComponent = (
				<UserDetailsContent
					company={props.company}
					user={selectedUser as FrontendUserInfo}
					goBackToViewUsersPage={goBackToViewUsersPage}
					adminInAction={adminInAction}
					setAddEditUserModalOpen={setAddEditUserModalOpen}
					setEditUserPermissionModalOpen={setEditUserPermissionModalOpen}
					setProceedAction={setProceedAction}
					operatorUser={props.user as FrontendUserInfo}
				></UserDetailsContent>
			);
			break;
	}

	return (
		<div style={{ margin: "0 auto" }}>
			{currentComponent}

			{
				<AddOrEditUserModal
					company={props.company}
					addOrEditUser={selectedUser ? editUser : createUser}
					operatorUser={props.user as FrontendUserInfo}
					open={addEditUserModalOpen}
					setOpen={setAddEditUserModalOpen}
					user={selectedUser}
					companyCustomGroups={companyCustomGroups}
				/>
			}
			{/* {
				<DeleteUserConfirmModal
					open={deleteUserConfirmOpen}
					setOpen={setDeleteUserConfirmOpen}
					proceedAction={proceedAction}
				></DeleteUserConfirmModal>
			} */}
		</div>
	);
}
